interface FreemiumSubscribePage {
  show_banner: boolean;
  skip_page: boolean;
}

/**
 * Safely retrieves and normalizes the freemium_subscribe_page data from localStorage.
 * Handles missing keys, invalid JSON, and string representations of boolean values.
 *
 * @returns FreemiumSubscribePage object with proper boolean values
 */
export function getFreemiumSubscribePageData(): FreemiumSubscribePage {
  const defaultData: FreemiumSubscribePage = {
    show_banner: false,
    skip_page: false,
  };

  try {
    const storedData = localStorage.getItem('freemium_subscribe_page');

    if (!storedData) {
      return defaultData;
    }

    const parsedData = JSON.parse(storedData);

    if (!parsedData || typeof parsedData !== 'object') {
      return defaultData;
    }

    return {
      show_banner: normalizeBooleanValue(parsedData.show_banner),
      skip_page: normalizeBooleanValue(parsedData.skip_page),
    };
  } catch (error) {
    // Return default values if JSON parsing fails
    return defaultData;
  }
}

/**
 * Normalizes a value to a boolean, handling string representations and undefined values.
 *
 * @param value - The value to normalize
 * @returns boolean value
 */
function normalizeBooleanValue(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    // Handle string representations
    const lowerValue = value.toLowerCase();
    if (lowerValue === 'true') return true;
    if (lowerValue === 'false') return false;
    if (lowerValue === 'undefined') return false;
  }

  // Handle other truthy/falsy values
  return Boolean(value);
}

/**
 * Sets the freemium_subscribe_page data in localStorage.
 *
 * @param data - Partial data to update (will merge with existing data)
 */
export function setFreemiumSubscribePageData(
  data: Partial<FreemiumSubscribePage>,
): void {
  try {
    const currentData = getFreemiumSubscribePageData();
    const updatedData = { ...currentData, ...data };
    localStorage.setItem(
      'freemium_subscribe_page',
      JSON.stringify(updatedData),
    );
  } catch (error) {
    // Silently fail if localStorage is not available
  }
}

/**
 * Removes the freemium_subscribe_page data from localStorage.
 */
export function removeFreemiumSubscribePageData(): void {
  try {
    localStorage.removeItem('freemium_subscribe_page');
  } catch (error) {
    // Silently fail if localStorage is not available
  }
}
